import React, { useCallback, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BookOpen, FileText, GraduationCap, Layers, Pencil, SearchX, Video } from "lucide-react";
import {
  AppButton, Callout, ConfirmModal, EmptyState, PageHeader, StatsCard, StatsGrid, Tabs, TabsContent, TabsList,
  TabsTrigger,
} from "../../ui";
import Shimmer from "../../Common/Shimmer";
import CommonToaster from "../../Common/CommonToaster";
import UploadMaterialModal from "../../Materials/UploadMaterialModal";
import useMaterialViewer from "../../Materials/useMaterialViewer";
import apiClient from "../../../utils/apiClient";
import { hasPermission } from "../../../utils/permissions";
import { usePlan } from "../../../contexts/PlanContext";
import { useBranch } from "../../../contexts/BranchContext";
import { formatDate } from "../../../utils/format";
import useCourseDetails from "./useCourseDetails";
import CourseIdentityCard from "./CourseIdentityCard";
import CourseVitals from "./CourseVitals";
import { CourseAboutCard, CourseFactsCard } from "./CourseOverviewCards";
import CourseBatchesTab from "./CourseBatchesTab";
import CourseSyllabusTab from "./CourseSyllabusTab";
import CourseFilesTab from "./CourseFilesTab";
import EditMaterialModal from "./EditMaterialModal";
import {
  attentionItems, courseFee, courseHighlights, courseSkills, isVideoFile, offeredAt, plural, splitFiles,
  syllabusSummary,
} from "./courseDetails";

const TAB_VALUES = ["batches", "syllabus", "videos", "materials"];

/** A count beside a tab label; read aloud as part of the tab's name. */
const TabCount = ({ n }) => (
  n === null || n === undefined ? null : (
    <span className="tw-ml-1.5 tw-rounded-full tw-bg-surface-sunken tw-px-1.5 tw-text-xs tw-font-medium tw-tabular-nums tw-text-content-muted">
      {n}
    </span>
  )
);

/** The skeleton mirrors the page, so nothing jumps when the course lands. */
const PageSkeleton = () => (
  <div className="tw-flex tw-flex-col tw-gap-4" aria-busy="true" aria-label="Loading course">
    <Shimmer height={104} />
    <StatsGrid cols={4}>
      {[0, 1, 2, 3].map((i) => <StatsCard key={i} loading />)}
    </StatsGrid>
    <div className="tw-grid tw-gap-4 min-[1200px]:tw-grid-cols-3">
      <div className="min-[1200px]:tw-col-span-2"><Shimmer height={200} /></div>
      <Shimmer height={200} />
    </div>
    <Shimmer height={40} />
  </div>
);

/**
 * CourseDetailsPage — `/courses/:id`, the admin's view of one batch course and
 * the place they run it from: its batches, the syllabus every batch is planned
 * from, and the videos and materials every batch shares.
 *
 * Reading order: which course and its actions; what needs attention (only
 * when something does); four vitals; what the course promises and how it is
 * set up; then a tab per job. The tab is in the URL (`?tab=syllabus`), so a
 * link can land on it and a refresh keeps it.
 *
 * The identity card carries only what acts on the COURSE (Edit course);
 * adding a batch acts on the list, so it sits in the Batches tab's toolbar.
 *
 * Permissions and plan features are decided HERE and nowhere below, against
 * the keys the API checks: `add_edit_courses` edits the course,
 * `manage_batches` adds a batch, `manage_syllabus` opens the syllabus builder.
 * Course files are open to every admin, as their routes are.
 */
const CourseDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPlanFeature } = usePlan();
  const { branches = [], isMultiBranch = false } = useBranch() || {};

  const can = {
    editCourse: hasPermission("add_edit_courses"),
    addBatch: hasPermission("manage_batches"),
    manageSyllabus: hasPermission("manage_syllabus"),
  };
  const hasWebsite = hasPlanFeature("cmsWebsite");

  const { course, batches, syllabus, headcount, files } = useCourseDetails(id);
  const { openMaterial, viewer } = useMaterialViewer();

  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const notify = useCallback((message, type = "success") => setToast({ show: true, message, type }), []);
  const [uploading, setUploading] = useState(null); // "video" | "document"
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const requestedTab = searchParams.get("tab");
  const tab = TAB_VALUES.includes(requestedTab) ? requestedTab : "batches";
  const setTab = useCallback((next) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === "batches") params.delete("tab");
      else params.set("tab", next);
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/courses"));
  const pageHeader = <PageHeader title="Course details" icon={GraduationCap} backButton onBack={goBack} />;
  const data = course.data;

  if (course.loading && !data) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {pageHeader}
        <PageSkeleton />
      </div>
    );
  }

  if (!data) {
    const notFound = course.error?.response?.status === 404 || (!course.error && !course.loading);
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {pageHeader}
        {notFound ? (
          <EmptyState
            icon={SearchX}
            title="Course not found"
            description="It may have been deleted, it may not be offered at your branch, or the link belongs to another institute."
            action={<AppButton variant="outline" onClick={() => navigate("/courses")}>Back to courses</AppButton>}
          />
        ) : (
          <Callout
            tone="danger"
            role="alert"
            action={<AppButton size="xs" variant="outline" onClick={course.reload}>Try again</AppButton>}
          >
            {course.error?.response?.data?.message || "Could not load this course."}
          </Callout>
        )}
      </div>
    );
  }

  /* ── A self-paced course has its own page ───────────────────────────────── */

  if (data.courseType === "self-paced") {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {pageHeader}
        <Callout
          tone="info"
          action={<AppButton size="xs" variant="outline" onClick={() => navigate(`/self-paced/${id}`)}>Open it</AppButton>}
        >
          “{data.name}” is a self-paced course. Its lessons, students and certificates are managed in the course builder.
        </Callout>
      </div>
    );
  }

  /* ── Derived ─────────────────────────────────────────────────────────────── */

  const fee = courseFee(data);
  const summary = syllabusSummary(syllabus.data);
  const { videos, documents } = splitFiles(files.data?.list || []);
  const filesShown = files.data?.list.length ?? 0;
  const truncated = files.data && files.data.total > filesShown ? { shown: filesShown, total: files.data.total } : null;
  const attention = attentionItems({
    batches: { known: !!batches.data, list: batches.data || [] },
    syllabus: { known: !syllabus.loading && !syllabus.error, summary },
    headcount: headcount.data,
  });

  const openBuilder = can.manageSyllabus ? () => navigate(`/courses/${id}/syllabus`) : undefined;
  const addBatch = can.addBatch ? () => navigate(`/batches/add?course=${id}`) : undefined;
  const editCourse = can.editCourse ? () => navigate(`/courses/edit/${id}`) : undefined;

  /* ── File actions ────────────────────────────────────────────────────────── */

  const failure = (err, fallback) => err?.response?.data?.message || fallback;

  // Staff may always download — the file's student access level does not bind
  // them — but a server that says otherwise is answered with the viewer.
  const downloadFile = async (material) => {
    try {
      const { data: res } = await apiClient.get(`/materials/${material._id}/download`);
      const link = res?.data;
      if (link?.url && link.downloadable !== false) window.open(link.url, "_blank", "noopener");
      else openMaterial(material);
    } catch (err) {
      notify(failure(err, "Could not download the file."), "error");
    }
  };

  const saveFile = async (body) => {
    await apiClient.put(`/materials/${editing._id}`, body);
    setEditing(null);
    notify("Changes saved.");
    files.reload();
  };

  const deleteFile = async () => {
    const material = deleting;
    setDeleting(null);
    try {
      await apiClient.delete(`/materials/${material._id}`);
      notify(`“${material.title}” deleted.`);
      files.reload();
    } catch (err) {
      notify(failure(err, "Could not delete the file."), "error");
    }
  };

  /* ── What to show ────────────────────────────────────────────────────────── */

  const filesTab = (kind) => (
    <CourseFilesTab
      kind={kind}
      files={kind === "video" ? videos : documents}
      loading={files.loading && !files.data}
      error={files.error}
      onRetry={files.reload}
      truncated={truncated}
      onUpload={() => setUploading(kind)}
      onOpen={openMaterial}
      onDownload={downloadFile}
      onEdit={setEditing}
      onDelete={setDeleting}
    />
  );

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {pageHeader}

      <CourseIdentityCard
        course={data}
        fee={fee}
        websiteListed={hasWebsite ? data.isPublished !== false : null}
        actions={editCourse && (
          <AppButton size="sm" variant="outline" onClick={editCourse}>
            <Pencil size={16} aria-hidden="true" /> Edit course
          </AppButton>
        )}
      />

      {attention.length > 0 && (
        <div className="tw-flex tw-flex-col tw-gap-2">
          {attention.map((item) => {
            if (item.key === "no-syllabus") {
              return (
                <Callout
                  key={item.key}
                  tone="warning"
                  action={openBuilder && <AppButton size="xs" variant="outline" onClick={openBuilder}>Create syllabus</AppButton>}
                >
                  This course has no syllabus yet, so a batch started from it gets no class plan.
                </Callout>
              );
            }
            if (item.key === "not-started") {
              const [first] = item.batches;
              return (
                <Callout
                  key={item.key}
                  tone="warning"
                  action={item.batches.length === 1 && (
                    <AppButton size="xs" variant="outline" onClick={() => navigate(`/batches/${first._id}`)}>Open batch</AppButton>
                  )}
                >
                  {item.batches.length === 1
                    ? `${first.name} was due to start on ${formatDate(first.startDate)} and has not been started, so it has no class plan yet.`
                    : `${plural(item.batches.length, "batch", "batches")} are past their start date and have not been started: ${item.batches.map((b) => b.name).join(", ")}.`}
                </Callout>
              );
            }
            if (item.key === "no-next") {
              return (
                <Callout
                  key={item.key}
                  tone="info"
                  action={addBatch && <AppButton size="xs" variant="outline" onClick={addBatch}>Add batch</AppButton>}
                >
                  Every batch of this course has finished, and no new one is scheduled.
                </Callout>
              );
            }
            return (
              <Callout key={item.key} tone="info">
                {item.count === 1
                  ? "1 student is enrolled in this course but is not in any of its batches."
                  : `${item.count} students are enrolled in this course but are not in any of its batches.`}
              </Callout>
            );
          })}
        </div>
      )}

      <CourseVitals
        headcount={headcount}
        batches={batches}
        syllabus={{ loading: syllabus.loading && !syllabus.data, summary, failed: !!syllabus.error }}
      />

      <div className="tw-grid tw-gap-4 min-[1200px]:tw-grid-cols-3">
        <div className="tw-min-w-0 min-[1200px]:tw-col-span-2">
          <CourseAboutCard
            course={data}
            highlights={courseHighlights(data.highlights)}
            skills={courseSkills(data)}
            onEdit={editCourse}
          />
        </div>
        <CourseFactsCard
          course={data}
          fee={fee}
          offered={isMultiBranch ? offeredAt(data, branches) : null}
          showWebsite={hasWebsite}
        />
      </div>

      <Tabs value={tab} onChange={setTab}>
        <TabsList overflow>
          <TabsTrigger value="batches">
            <Layers size={15} className="tw-mr-1" aria-hidden="true" /> Batches
            <TabCount n={batches.data ? batches.data.length : null} />
          </TabsTrigger>
          <TabsTrigger value="syllabus">
            <BookOpen size={15} className="tw-mr-1" aria-hidden="true" /> Syllabus
          </TabsTrigger>
          <TabsTrigger value="videos">
            <Video size={15} className="tw-mr-1" aria-hidden="true" /> Videos
            <TabCount n={files.data ? videos.length : null} />
          </TabsTrigger>
          <TabsTrigger value="materials">
            <FileText size={15} className="tw-mr-1" aria-hidden="true" /> Materials
            <TabCount n={files.data ? documents.length : null} />
          </TabsTrigger>
        </TabsList>

        <TabsContent value="batches">
          <CourseBatchesTab batches={batches} headcount={headcount} onAdd={addBatch} />
        </TabsContent>

        <TabsContent value="syllabus">
          <CourseSyllabusTab syllabus={syllabus} summary={summary} onManage={openBuilder} />
        </TabsContent>

        <TabsContent value="videos">{filesTab("video")}</TabsContent>

        <TabsContent value="materials">{filesTab("document")}</TabsContent>
      </Tabs>

      <UploadMaterialModal
        show={!!uploading}
        onClose={() => setUploading(null)}
        courseId={id}
        defaultLevel="course"
        hideLevel
        videosOnly={uploading === "video"}
        onSuccess={() => {
          setUploading(null);
          notify(uploading === "video" ? "Video uploaded." : "Material uploaded.");
          files.reload();
        }}
      />

      <EditMaterialModal material={editing} onHide={() => setEditing(null)} onSave={saveFile} />

      <ConfirmModal
        show={!!deleting}
        onHide={() => setDeleting(null)}
        onConfirm={deleteFile}
        title={isVideoFile(deleting) ? "Delete video" : "Delete material"}
        message={`Delete “${deleting?.title}”? Every batch of this course loses it.`}
        confirmText="Delete"
        variant="danger"
      />

      {viewer}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default CourseDetailsPage;
